// Géométries vanilla (joueur + armures) codées en dur : elles ne sont pas livrées
// dans les packs (built-in Minecraft), on les reconstruit ici pour l'aperçu.
// Le personnage regarde vers +Z (front = face south), bras droit en -X.
import { loadImageDirect } from './BedrockModelViewer';

type Face = { uv: [number, number]; uv_size: [number, number] };
type PerFaceCube = { origin: number[]; size: number[]; uv: Record<string, Face> };
type Bone = { name: string; parent?: string; pivot?: number[]; cubes: PerFaceCube[] };
export type GeoEntry = { description: { texture_width: number; texture_height: number }; bones: Bone[] };

export type ArmorSlot = 'helmet' | 'chestplate' | 'leggings' | 'boots';

/** Cube en "box UV" (uv = coin [U,V]) converti au format per-face consommé par buildGeometryMesh. */
function boxCube(origin: number[], size: number[], uv: [number, number], inflate = 0): PerFaceCube {
  const [ox, oy, oz] = origin as [number, number, number];
  const [w, h, d] = size as [number, number, number];
  const [U, V] = uv;
  return {
    origin: [ox - inflate, oy - inflate, oz - inflate],
    size: [w + 2 * inflate, h + 2 * inflate, d + 2 * inflate],
    uv: {
      west: { uv: [U, V + d], uv_size: [d, h] },
      south: { uv: [U + d, V + d], uv_size: [w, h] }, // face avant (vers +Z)
      east: { uv: [U + d + w, V + d], uv_size: [d, h] },
      north: { uv: [U + 2 * d + w, V + d], uv_size: [w, h] },
      up: { uv: [U + d, V], uv_size: [w, d] },
      down: { uv: [U + d + w, V], uv_size: [w, d] },
    },
  };
}

/** Modèle joueur 64x64 (Steve), pieds à y=0, sommet de la tête à y=32 px. */
export function playerGeometry(): GeoEntry {
  return {
    description: { texture_width: 64, texture_height: 64 },
    bones: [
      { name: 'body', pivot: [0, 24, 0], cubes: [boxCube([-4, 12, -2], [8, 12, 4], [16, 16])] },
      { name: 'head', pivot: [0, 24, 0], cubes: [boxCube([-4, 24, -4], [8, 8, 8], [0, 0])] },
      { name: 'rightArm', pivot: [-5, 22, 0], cubes: [boxCube([-8, 12, -2], [4, 12, 4], [40, 16])] },
      { name: 'leftArm', pivot: [5, 22, 0], cubes: [boxCube([4, 12, -2], [4, 12, 4], [32, 48])] },
      { name: 'rightLeg', pivot: [-1.9, 12, 0], cubes: [boxCube([-3.9, 0, -2], [4, 12, 4], [0, 16])] },
      { name: 'leftLeg', pivot: [1.9, 12, 0], cubes: [boxCube([-0.1, 0, -2], [4, 12, 4], [16, 48])] },
    ],
  };
}

/** Modèle d'armure (couche sur le corps) pour un slot, texture 64x32 (layer 1) ou _2 (leggings). */
export function armorGeometry(slot: ArmorSlot): GeoEntry {
  const base = { description: { texture_width: 64, texture_height: 32 } };
  const bone = (cubes: PerFaceCube[]): GeoEntry => ({ ...base, bones: [{ name: 'armor', cubes }] });
  switch (slot) {
    case 'helmet':
      return bone([boxCube([-4, 24, -4], [8, 8, 8], [0, 0], 1)]);
    case 'chestplate':
      return bone([
        boxCube([-4, 12, -2], [8, 12, 4], [16, 16], 1.02),
        boxCube([-8, 12, -2], [4, 12, 4], [40, 16], 1),
        boxCube([4, 12, -2], [4, 12, 4], [40, 16], 1),
      ]);
    case 'leggings':
      return bone([
        boxCube([-4, 12, -2], [8, 12, 4], [16, 16], 0.5),
        boxCube([-3.9, 0, -2], [4, 12, 4], [0, 16], 0.5),
        boxCube([-0.1, 0, -2], [4, 12, 4], [0, 16], 0.5),
      ]);
    case 'boots':
      return bone([
        boxCube([-3.9, 0, -2], [4, 12, 4], [0, 16], 1),
        boxCube([-0.1, 0, -2], [4, 12, 4], [0, 16], 1),
      ]);
  }
}

/** Extrait une entrée de géométrie exploitable par buildGeometryMesh, format 1.12+ ou 1.8. */
export function extractGeoEntry(geoJson: any, geometryId?: string | null): any {
  if (!geoJson) return null;
  const arr = geoJson['minecraft:geometry'];
  if (Array.isArray(arr) && arr.length) {
    return (geometryId && arr.find((g: any) => g?.description?.identifier === geometryId)) || arr[0];
  }
  // Ancien format : { "geometry.xxx": { texturewidth, textureheight, bones } }
  let old: any = geometryId ? geoJson[geometryId] : null;
  if (!old) {
    const key = Object.keys(geoJson).find((k) => k.startsWith('geometry.'));
    old = key ? geoJson[key] : null;
  }
  if (old && (old.bones || old.cubes)) {
    return {
      description: {
        texture_width: old.texturewidth || old.texture_width || 64,
        texture_height: old.textureheight || old.texture_height || 32,
      },
      bones: old.bones || [],
    };
  }
  return null;
}

/** Déduit le slot d'armure depuis un nom de géométrie / identifiant, sinon null. */
export function detectArmorSlot(...hints: (string | null | undefined)[]): ArmorSlot | null {
  const h = hints.filter(Boolean).join(' ').toLowerCase();
  if (h.includes('helmet') || h.includes('cap') || h.includes('_head')) return 'helmet';
  if (h.includes('chestplate') || h.includes('chest')) return 'chestplate';
  if (h.includes('leggings') || h.includes('legging')) return 'leggings';
  if (h.includes('boots') || h.includes('boot')) return 'boots';
  return null;
}

let skinPromise: Promise<HTMLImageElement | null> | null = null;

/** Skin mannequin "Steve-like" généré (64x64) : sert de support pour visualiser armures/items. */
export function loadMannequinSkin(): Promise<HTMLImageElement | null> {
  if (skinPromise) return skinPromise;
  skinPromise = (async () => {
    const c = document.createElement('canvas');
    c.width = 64;
    c.height = 64;
    const x = c.getContext('2d');
    if (!x) return null;
    const fill = (col: string, rx: number, ry: number, rw: number, rh: number) => {
      x.fillStyle = col;
      x.fillRect(rx, ry, rw, rh);
    };
    const SKIN = '#c8956c';
    const HAIR = '#4a3320';
    const SHIRT = '#2f8f8f';
    const PANTS = '#39397e';
    const EYE = '#3b2a1a';
    const WHITE = '#e2e2e2';
    // Tête (bloc 0,0 -> 32x16) : peau + cheveux dessus/derrière + yeux sur la face avant (tuile south à 8,8).
    fill(SKIN, 0, 0, 32, 16);
    fill(HAIR, 8, 0, 8, 8);
    fill(HAIR, 24, 8, 8, 8);
    fill(HAIR, 0, 8, 2, 8);
    fill(HAIR, 14, 8, 2, 8);
    fill(WHITE, 10, 10, 2, 2);
    fill(WHITE, 13, 10, 2, 2);
    fill(EYE, 11, 10, 1, 2);
    fill(EYE, 14, 10, 1, 2);
    // Corps (chemise) + bras (peau, manches courtes) + jambes (pantalon).
    fill(SHIRT, 16, 16, 24, 16);
    fill(SKIN, 40, 16, 16, 16);
    fill(SHIRT, 40, 16, 16, 5);
    fill(SKIN, 32, 48, 16, 16);
    fill(SHIRT, 32, 48, 16, 5);
    fill(PANTS, 0, 16, 16, 16);
    fill(PANTS, 16, 48, 16, 16);
    return loadImageDirect(c.toDataURL());
  })();
  return skinPromise;
}
